import { isEmpty, safeMerge } from "../common/utilities";
import { getAllComponentsTheme, getFont, getTypographyTheme } from "../themes/common/utilities";
/**
 * Dynamically load typography styles.
 *
 * @param {String} baseTheme the name of the built-in theme
 * @param {Object} customTheme an object representing a customization
 *                             of typography.
 * @returns an object representing the typography styles for
 * the given theme and the given custom theme options.
 *
 * @async
 *
 */

export async function createTypographyStyles(_ref) {
  let {
    customTheme,
    baseTheme
  } = _ref;
  const defaultTheme = await getTypographyTheme(baseTheme);
  /**
   * If there is a custom typography, we need to
   * merge it with the default typography.
   */

  const typography = customTheme !== null && customTheme !== void 0 && customTheme.typography ? safeMerge(defaultTheme.typography, customTheme === null || customTheme === void 0 ? void 0 : customTheme.typography) : defaultTheme.typography;
  return {
    typography
  };
}
/**
 * Dynamically load components styles.
 *
 * @param {String} baseTheme the name of the built-in theme
 * @param {Object} customTheme an object representing a customization
 *                             of one or more components.
 * @returns an object representing all components styles for
 * the given theme and the given custom theme options.
 *
 * @async
 *
 */

export async function createComponentsStyles(_ref2) {
  let {
    customTheme,
    baseTheme
  } = _ref2;
  const defaultTheme = await getAllComponentsTheme(baseTheme);
  let components = {};
  Object.keys(defaultTheme).forEach(component => {
    components[component] = typeof defaultTheme[component] === "function" ? defaultTheme[component](customTheme === null || customTheme === void 0 ? void 0 : customTheme.tokens) : defaultTheme[component];
  });
  components = safeMerge({
    components,
    spacing: defaultTheme.spacing
  }, customTheme !== null && customTheme !== void 0 && customTheme.components ? customTheme : null);
  return {
    components
  };
}
/**
 * Dynamically load custom fonts.
 *
 * @param {String | String[]} customFonts name of the font(s) to be
 *                                        loaded.
 * @returns an array of fonts ready to be loaded into the Emotion
 * Global component.
 *
 * @async
 *
 */

export async function loadFonts(customFonts) {
  if (typeof customFonts === "string") {
    const f1 = await getFont(customFonts);
    return f1 ? [f1] : null;
  }

  if (!isEmpty(customFonts)) {
    const allFonts = [];

    for (const name of customFonts) {
      const font = await getFont(name);

      if (font) {
        allFonts.push(font);
      }
    }

    return allFonts;
  }

  return null;
}